import { Component, OnInit, Inject, OnDestroy, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus, PopupRequest, RedirectRequest, EventMessage, EventType, InteractionType, AccountInfo, SsoSilentRequest, PromptValue } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, finalize, takeUntil, timeout } from 'rxjs/operators';

import { environment } from '../environments/environment';
import { MenuItem, PrimeNGConfig } from 'primeng/api';
import { Router } from '@angular/router';
import { AppConfig, LayoutService } from './modules/layout/service/app.layout.service';
import { TranslateService } from '@ngx-translate/core';

import { UserDTO } from './interfaces/base-entity';
import { StorageService } from './modules/shared/services/storage.service';
import { SgsstAuthUserService } from './modules/shared/services/auth.service';
import { SessionLogoutService } from './modules/shared/services/session-logout.service';
import { SgsstAuthService } from './modules/epps/services/auth-service.service';
import { NotificationService } from './modules/notifications/services/notification.service';
import { UserRoleEnum, StorageKeys } from './enums/enums';
import { normalizeSessionUser } from './utils/session-user.util';
import { CorpacerosSyncService } from './modules/epps/services/corpaceros-sync.service';
import {
    clearTenantContext,
    hasValidTenantContext,
    persistTenantContext,
    readTenantPreference,
    resolveSessionTenantContext
} from './utils/session-tenant-context.util';
import {
    hasMsalAccount,
    isB2cPolicy,
    resolveLoginRedirectUri,
    resolveMsalIdTokenClaims,
    shouldClearAppSessionOnAccountsRemoved
} from './utils/msal-auth.util';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [TranslateService]
})
export class AppComponent implements OnInit, OnDestroy {
    userRoles = UserRoleEnum;
    title = 'SGSST Seguros Bolivar';
    isIframe = false;
    loginDisplay = false;
    private readonly _destroying$ = new Subject<void>();
    items: MenuItem[] | undefined;
    currentUser: UserDTO | undefined;
    private isAuthChecked = false;
    private interactionStatus: InteractionStatus = InteractionStatus.None;
    private authService: MsalService = inject(MsalService);
    private msalBroadcastService: MsalBroadcastService = inject(MsalBroadcastService);
    private primengConfig: PrimeNGConfig = inject(PrimeNGConfig);
    private layoutService: LayoutService = inject(LayoutService);
    private translateService: TranslateService = inject(TranslateService);
    private userService = inject(SgsstAuthService);
    private storage = inject(StorageService);
    private authSsgtService: SgsstAuthUserService = inject(SgsstAuthUserService);
    private sessionLogout = inject(SessionLogoutService);
    public corpacerosSyncService = inject(CorpacerosSyncService);
    public notificationService = inject(NotificationService);
    constructor(private router: Router, @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration, private titleService: Title) {
        this.titleService.setTitle(environment?.pageTitle ?? 'demo');
    }


    translate(lang: string) {
        this.translateService.use(lang);
        this.translateService.get('es').subscribe(res => {
            this.primengConfig.setTranslation(res);

        });
    }


    private applySessionUserWithoutTenant(userId: string): void {
        clearTenantContext(this.storage);
        const minimalUser = {
            identityId: userId,
            oidB2C: userId
        } as UserDTO;
        this.currentUser = minimalUser;
        this.storage.setItem(StorageKeys.CurrentUser, minimalUser, true);
        this.authSsgtService.userLoggedIn$.next(minimalUser);
    }

    private resolveUserIdFromAccount(account: AccountInfo | null): string | null {
        if (!account) {
            return null;
        }
        return (
            (account.idTokenClaims?.oid as string | undefined) ??
            (account.idTokenClaims?.sub as string | undefined) ??
            account.localAccountId ??
            null
        );
    }

    /**
     * Garantiza checkUserAuth tras login o refresh con sesión MSAL ya cacheada
     * (en refresh a menudo no llega ACQUIRE_TOKEN_SUCCESS de inmediato).
     */
    private ensureSgsstSessionResolved(): void {
        if (this.isAuthChecked) {
            return;
        }

        this.checkAndSetActiveAccount();
        const account =
            this.authService.instance.getActiveAccount() ??
            this.authService.instance.getAllAccounts()[0] ??
            null;

        if (!account) {
            this.authSsgtService.markTenantResolutionResolved();
            return;
        }

        const userId = this.resolveUserIdFromAccount(account);
        if (!userId) {
            console.error('AppComponent: no se pudo resolver userId del token MSAL');
            this.authSsgtService.markTenantResolutionResolved();
            return;
        }

        this.isAuthChecked = true;
        this.resolveSgsstSession(userId);
    }

    private resolveSgsstSession(userId: string): void {
        this.authSsgtService.markTenantResolutionPending();
        this.userService.checkUserAuth(userId).pipe(
            timeout(20000),
            finalize(() => this.authSsgtService.markTenantResolutionResolved())
        ).subscribe({
            next: (user: UserDTO) => {
                const sessionUser = normalizeSessionUser(user);
                if (sessionUser) {
                    this.currentUser = sessionUser;
                    this.storage.setItem(StorageKeys.CurrentUser, sessionUser, true);
                    const tenantContext = resolveSessionTenantContext(
                        sessionUser,
                        readTenantPreference(this.storage)
                    );
                    if (tenantContext) {
                        persistTenantContext(this.storage, tenantContext);
                    } else {
                        clearTenantContext(this.storage);
                        console.warn('AppComponent: usuario autenticado sin compañía o rol válido.');
                    }

                    if (!sessionUser.isActive) {
                        console.warn('AppComponent: usuario autenticado marcado como inactivo en la base de datos.');
                    }
                } else {
                    clearTenantContext(this.storage);
                }
                this.authSsgtService.userLoggedIn$.next(sessionUser ?? user);
                this.tryLoadNotificationCount();
            },
            error: (error) => {
                console.error('AppComponent: Error verificando usuario:', error);
                this.applySessionUserWithoutTenant(userId);
            }
        });
    }

    private tryLoadNotificationCount(): void {
        if (!hasValidTenantContext(this.storage)) {
            return;
        }
        this.notificationService.getNotificationsCount().subscribe({
            next: (data: any) => {
                if (data && data.success) {
                    this.notificationService.notificationsInfo$.next({ info: data.data });
                }
            },
            error: (error: any) => {
                console.error('Error fetching notification count:', error);
            }
        });
    }


    ngOnInit(): void {

        this.isAuthChecked = false;
        this.authSsgtService.userLoggedIn$.subscribe((user: UserDTO | null) => {
            this.currentUser = user ?? undefined;
        });

        //optional configuration with the default configuration
        const config: AppConfig = {
            ripple: false,                      //toggles ripple on and off
            inputStyle: 'outlined',             //default style for input elements
            menuMode: 'overlay',                 //layout mode of the menu, valid values are "static" and "overlay"
            colorScheme: 'dark',               //color scheme of the template, valid values are "light" and "dark"
            theme: 'saga-green',         //default component theme for PrimeNG
            scale: 12                           //size of the body font size to scale the whole application
        };
        this.layoutService.config.set(config);
        this.translateService.setDefaultLang('es');

        this.primengConfig.ripple = true;
        this.translate('es');
        this.setLoginDisplay();

        this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal
        this.setLoginDisplay();

        this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window

        this.msalBroadcastService.inProgress$
            .pipe(takeUntil(this._destroying$))
            .subscribe((status: InteractionStatus) => {
                this.interactionStatus = status;
            });

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                const accountCount = this.authService.instance.getAllAccounts().length;
                if (shouldClearAppSessionOnAccountsRemoved(accountCount, this.interactionStatus)) {
                    // No re-disparar end-session: ya no hay cuenta MSAL (otra pestaña o logout en curso).
                    this.sessionLogout.onAccountsCleared();
                }
                this.setLoginDisplay();
            });

        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                this.setLoginDisplay();
                this.checkAndSetActiveAccount();
                if (this.authService.instance.getAllAccounts().length === 0) {
                    this.authSsgtService.markTenantResolutionResolved();
                    return;
                }
                this.ensureSgsstSessionResolved();
                this.leaveLoggedOutIfAuthenticated();
            });

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
                    || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
                    || msg.eventType === EventType.SSO_SILENT_SUCCESS),
                takeUntil(this._destroying$)
            )
            .subscribe((result: EventMessage) => {
                try {
                    const payload = result.payload as AuthenticationResult;
                    const idtoken = resolveMsalIdTokenClaims(payload);

                    if (payload?.account) {
                        this.authService.instance.setActiveAccount(payload.account);
                    }

                    this.ensureSgsstSessionResolved();
                    this.leaveLoggedOutIfAuthenticated();

                    if (isB2cPolicy(idtoken, environment.b2cPolicies.names.signUpSignIn)) {
                        this.authService.instance.setActiveAccount(payload.account);
                    }

                    if (isB2cPolicy(idtoken, environment.b2cPolicies.names.editProfile)) {
                        const originalSignInAccount = this.authService.instance.getAllAccounts()
                            .find((account: AccountInfo) =>
                                account.idTokenClaims?.oid === idtoken?.oid
                                && account.idTokenClaims?.sub === idtoken?.sub
                                && isB2cPolicy(
                                    account.idTokenClaims as ReturnType<typeof resolveMsalIdTokenClaims>,
                                    environment.b2cPolicies.names.signUpSignIn
                                )
                            );

                        const signUpSignInFlowRequest: SsoSilentRequest = {
                            authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
                            account: originalSignInAccount
                        };

                        this.authService.ssoSilent(signUpSignInFlowRequest);
                    }

                    if (isB2cPolicy(idtoken, environment.b2cPolicies.names.resetPassword)) {
                        const signUpSignInFlowRequest: RedirectRequest | PopupRequest = {
                            authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
                            scopes: [...environment.apiConfig.scopes],
                            prompt: PromptValue.LOGIN
                        };

                        this.login(signUpSignInFlowRequest);
                    }
                } catch (error) {
                    console.error('AppComponent: Error procesando evento MSAL:', error);
                }
            });

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
                takeUntil(this._destroying$)
            )
            .subscribe((result: EventMessage) => {
                // Check for forgot password error
                // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
                if (result.error && result.error.message.indexOf('AADB2C90118') > -1) {
                    let resetPasswordFlowRequest: RedirectRequest | PopupRequest = {
                        authority: environment.b2cPolicies.authorities.resetPassword.authority,
                        scopes: [],
                    };

                    this.login(resetPasswordFlowRequest);
                };
            });
    }

    setLoginDisplay() {
        this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
    }

    /**
     * Si el IdP devolvió el code a /logged-out (URI mal registrada o confusión post-logout),
     * salir de esa pantalla cuando ya hay cuenta MSAL.
     */
    private leaveLoggedOutIfAuthenticated(): void {
        if (!this.router.url.startsWith('/logged-out')) {
            return;
        }
        if (!hasMsalAccount(this.authService.instance.getAllAccounts())) {
            return;
        }
        void this.router.navigateByUrl('/');
    }

    checkAndSetActiveAccount() {
        /**
         * If no active account set but there are accounts signed in, sets first account to active account
         * To use active account set here, subscribe to inProgress$ first in your component
         * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
         */
        let activeAccount = this.authService.instance.getActiveAccount();

        if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
            let accounts = this.authService.instance.getAllAccounts();
            this.authService.instance.setActiveAccount(accounts[0]);
        }
    }

    loginRedirect() {
        const redirectUri = resolveLoginRedirectUri();
        if (this.msalGuardConfig.authRequest) {
            this.authService.loginRedirect({
                ...this.msalGuardConfig.authRequest,
                redirectUri
            } as RedirectRequest).subscribe({
                error: (err) => console.error('MSAL loginRedirect failed', err)
            });
        } else {
            this.authService.loginRedirect({
                scopes: [...environment.apiConfig.scopes],
                redirectUri
            }).subscribe({
                error: (err) => console.error('MSAL loginRedirect failed', err)
            });
        }
    }

    login(userFlowRequest?: RedirectRequest | PopupRequest) {
        const redirectUri = resolveLoginRedirectUri();

        if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
            if (this.msalGuardConfig.authRequest) {
                this.authService.loginPopup({
                    ...this.msalGuardConfig.authRequest,
                    ...userFlowRequest,
                    redirectUri
                } as PopupRequest)
                    .subscribe((response: AuthenticationResult) => {
                        this.authService.instance.setActiveAccount(response.account);
                    });
            } else {
                this.authService.loginPopup({ ...userFlowRequest, redirectUri } as PopupRequest)
                    .subscribe((response: AuthenticationResult) => {
                        this.authService.instance.setActiveAccount(response.account);
                    });
            }
        } else {
            if (this.msalGuardConfig.authRequest) {
                this.authService.loginRedirect({
                    ...this.msalGuardConfig.authRequest,
                    ...userFlowRequest,
                    redirectUri
                } as RedirectRequest).subscribe({
                    error: (err) => console.error('MSAL loginRedirect failed', err)
                });
            } else {
                this.authService.loginRedirect({ ...userFlowRequest, redirectUri } as RedirectRequest).subscribe({
                    error: (err) => console.error('MSAL loginRedirect failed', err)
                });
            }
        }
    }

    logout() {
        this.sessionLogout.logout();
    }

    editProfile() {
        let editProfileFlowRequest: RedirectRequest | PopupRequest = {
            authority: environment.b2cPolicies.authorities.editProfile.authority,
            scopes: [],
        };

        this.login(editProfileFlowRequest);
    }

    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }
}